import { ReglagesTabs } from '@/components/admin/reglages/reglages-tabs';
import { useAdminSession, useAdminSignOut } from '@/lib/admin-session';
import { useReplaySettings } from '@/lib/use-replay-settings';
import { Outlet, createFileRoute } from '@tanstack/react-router';
import { LogOut } from 'lucide-react';

/**
 * The Réglages shell (BUILD-851, reduced by the coupe of issue #32, given its
 * bar back by issue #41).
 *
 * ## The bar and the title move together, in both directions
 *
 * It carried four sub-tabs - Modèles, Ambiance, Scripts, Seuils. The coupe
 * removed three and, with them, the BAR and the `h1`: a lone tab is not a
 * navigation, and without a bar the only screen served here is the operator's,
 * so `reglages.modeles.tsx` took its own `h1` back - two `h1` on one page name
 * two pages.
 *
 * §11.5 brings a second tab, Personas, and the gesture inverts exactly: the bar
 * comes back, the `h1` « Réglages » comes back here, and each sub-page drops to
 * an `h2`. This is what `e2e/tests/admin-shell.spec.ts` now reads at level 1 on
 * the Réglages entry of the rail.
 *
 * What the shell keeps from the coupe: the sign-out on a phone. The rail folded
 * into a bottom bar does not carry it (issue #24, S2), so this is where, and
 * nowhere else, a session is closed from a phone.
 */

export const Route = createFileRoute('/admin/reglages')({
  component: ReglagesLayout,
});

function ReglagesLayout() {
  // The eighth tab exists only where the replay is open (rejeu.md §4, issue
  // #193): the same read as the tab's own page, so one request answers both. A
  // 404 reads `null`, an unread or failed one reads `undefined` - the bar then
  // stays the seven of every server rather than guessing.
  const replay = useReplaySettings();
  return (
    <section className="space-y-6">
      <header className="space-y-4">
        <h1 className="font-bold text-3xl text-bleu-nuit">Réglages</h1>
        <ReglagesTabs replayOpen={replay.data != null} />
      </header>
      <Outlet />
      <SignOutOnPhone />
    </section>
  );
}

function SignOutOnPhone() {
  const { email } = useAdminSession();
  const { signOut, signingOut } = useAdminSignOut();

  // `null` is a machine, not an absence to hide (T6): the shared token names
  // nobody, so there is no session to close.
  if (email === null) return null;

  return (
    <div className="border-gray-200 border-t pt-6 md:hidden">
      <button
        type="button"
        onClick={signOut}
        disabled={signingOut}
        className="inline-flex h-10 items-center gap-2 rounded-sm border border-gray-200 px-4 font-semibold text-bleu-nuit text-sm disabled:text-gray-400"
      >
        <LogOut size={18} strokeWidth={2} aria-hidden="true" />
        Se déconnecter
      </button>
    </div>
  );
}
